import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaLightbulb, FaChartLine, FaUsers, FaRocket } from 'react-icons/fa';
import Section from '../../components/common/Section';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';

const PageContainer = styled(motion.div)`
  padding-top: 100px;
`;

const HeroSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space['3xl']} 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.03;
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['5xl']};
  margin-bottom: ${({ theme }) => theme.space.lg};
  position: relative;
  display: inline-block;
  
  span {
    color: ${({ theme }) => theme.colors.accent};
    font-family: ${({ theme }) => theme.fonts.accent};
    font-style: italic;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes['4xl']};
  }
`;

const HeroDescription = styled.p`
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  margin-top: ${({ theme }) => theme.space['2xl']};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const ServiceTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  margin-bottom: ${({ theme }) => theme.space.md};
  color: ${({ theme }) => theme.colors.primary};
`;

const ServiceDescription = styled.p`
  margin-bottom: ${({ theme }) => theme.space.lg};
  line-height: 1.7;
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.white};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
`;

const ApproachSection = styled.div`
  margin-top: ${({ theme }) => theme.space['3xl']};
`;

const ApproachTitle = styled.h2`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ApproachSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.space.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ApproachStep = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.space.xl};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  border-top: 4px solid transparent;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    border-top: 4px solid ${({ theme }) => theme.colors.accent};
  }
`;

const StepNumber = styled.div`
  position: absolute;
  top: -20px;
  left: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradient.accent};
  color: ${({ theme }) => theme.colors.dark};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const StepTitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  margin-bottom: ${({ theme }) => theme.space.sm};
  color: ${({ theme }) => theme.colors.primary};
  margin-top: ${({ theme }) => theme.space.md};
`;

const StepDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.grey[700]};
  line-height: 1.6;
`;

const TestimonialsSection = styled.div`
  margin-top: ${({ theme }) => theme.space['3xl']};
  background: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.space['2xl']} 0;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const TestimonialsTitle = styled.h2`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const Testimonial = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.space.xl};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  margin-bottom: ${({ theme }) => theme.space.xl};
  position: relative;
  
  &::before {
    content: '"';
    position: absolute;
    top: 10px;
    left: 20px;
    font-size: 60px;
    color: ${({ theme }) => theme.colors.accent};
    font-family: ${({ theme }) => theme.fonts.accent};
    opacity: 0.2;
  }
`;

const TestimonialText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.grey[700]};
  margin-bottom: ${({ theme }) => theme.space.md};
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorImage = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.grey[300]};
  margin-right: ${({ theme }) => theme.space.md};
  background-size: cover;
  background-position: center;
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.h5`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 2px;
`;

const AuthorTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.grey[600]};
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: ${({ theme }) => theme.space['3xl']};
  padding: ${({ theme }) => theme.space['2xl']} ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => theme.colors.gradient.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.white};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/assets/images/pattern.svg');
    opacity: 0.05;
    pointer-events: none;
  }
`;

const CTATitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  margin-bottom: ${({ theme }) => theme.space.lg};
`;

const CTADescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  line-height: 1.7;
`;

const StrategicConsulting: React.FC = () => {
  return (
    <PageContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Section>
        <HeroSection>
          <HeroTitle>Strategic <span>Consulting</span></HeroTitle>
          <HeroDescription>
            Navigate complex business challenges and seize opportunities with our expert strategic consulting services. We partner with you to develop tailored solutions that drive sustainable growth.
          </HeroDescription>
          <Button variant="accent" size="large">Schedule a Consultation</Button>
        </HeroSection>
        
        <ServicesGrid>
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaLightbulb />
            </IconWrapper>
            <ServiceTitle>Business Strategy Development</ServiceTitle>
            <ServiceDescription>
              We help you craft comprehensive business strategies that align with your vision and goals. Our approach combines industry expertise, data analysis, and innovative thinking to create a roadmap for success.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaChartLine />
            </IconWrapper>
            <ServiceTitle>Growth & Expansion Planning</ServiceTitle>
            <ServiceDescription>
              Whether you're looking to enter new markets, launch new products, or scale your operations, our growth planning services help you identify and capitalize on opportunities while minimizing risks.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaUsers />
            </IconWrapper>
            <ServiceTitle>Organizational Transformation</ServiceTitle>
            <ServiceDescription>
              Navigate change successfully with our organizational transformation services. We help you adapt to evolving market conditions, implement new technologies, and optimize your operational structure.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
          
          <Card elevation="medium" hoverEffect>
            <IconWrapper>
              <FaRocket />
            </IconWrapper>
            <ServiceTitle>Digital Strategy & Innovation</ServiceTitle>
            <ServiceDescription>
              Stay ahead of the digital curve with strategies that leverage emerging technologies and innovative approaches. We help you identify digital opportunities and develop a roadmap for implementation.
            </ServiceDescription>
            <Button variant="text">Learn More</Button>
          </Card>
        </ServicesGrid>
        
        <ApproachSection>
          <ApproachTitle>Our Consulting Approach</ApproachTitle>
          <ApproachSteps>
            <ApproachStep>
              <StepNumber>1</StepNumber>
              <StepTitle>Discovery & Assessment</StepTitle>
              <StepDescription>
                We begin by thoroughly understanding your business, industry, challenges, and goals. Our team conducts comprehensive assessments to identify strengths, weaknesses, opportunities, and threats.
              </StepDescription>
            </ApproachStep>
            
            <ApproachStep>
              <StepNumber>2</StepNumber>
              <StepTitle>Strategy Development</StepTitle>
              <StepDescription>
                Based on our findings, we develop tailored strategies and solutions that address your specific challenges and align with your business objectives. We collaborate closely with your team throughout this process.
              </StepDescription>
            </ApproachStep>
            
            <ApproachStep>
              <StepNumber>3</StepNumber>
              <StepTitle>Implementation Planning</StepTitle>
              <StepDescription>
                We create detailed implementation plans that outline the steps, resources, timelines, and metrics needed to execute the strategy successfully. Our plans are practical, actionable, and designed for results.
              </StepDescription>
            </ApproachStep>
            
            <ApproachStep>
              <StepNumber>4</StepNumber>
              <StepTitle>Execution Support</StepTitle>
              <StepDescription>
                Our team provides hands-on support during the implementation phase, helping you navigate challenges, make adjustments, and maintain momentum. We ensure that strategies translate into tangible outcomes.
              </StepDescription>
            </ApproachStep>
            
            <ApproachStep>
              <StepNumber>5</StepNumber>
              <StepTitle>Measurement & Refinement</StepTitle>
              <StepDescription>
                We establish clear metrics to track progress and measure success. Through regular reviews and analysis, we refine strategies to optimize results and adapt to changing conditions.
              </StepDescription>
            </ApproachStep>
            
            <ApproachStep>
              <StepNumber>6</StepNumber>
              <StepTitle>Knowledge Transfer</StepTitle>
              <StepDescription>
                We believe in empowering your team with the knowledge and skills needed for long-term success. Throughout our engagement, we focus on building your internal capabilities and ensuring sustainable results.
              </StepDescription>
            </ApproachStep>
          </ApproachSteps>
        </ApproachSection>
        
        <TestimonialsSection>
          <TestimonialsTitle>Client Success Stories</TestimonialsTitle>
          
          <Testimonial>
            <TestimonialText>
              "The strategic guidance provided by InsightEdge transformed our approach to market expansion. Their data-driven insights and practical recommendations helped us enter three new markets successfully, resulting in a 45% increase in revenue within 18 months."
            </TestimonialText>
            <TestimonialAuthor>
              <AuthorImage style={{ backgroundImage: 'url(/assets/images/testimonials/client1.jpg)' }} />
              <AuthorInfo>
                <AuthorName>Sarah Johnson</AuthorName>
                <AuthorTitle>CEO, TechGrowth Solutions</AuthorTitle>
              </AuthorInfo>
            </TestimonialAuthor>
          </Testimonial>
          
          <Testimonial>
            <TestimonialText>
              "Working with InsightEdge on our digital transformation initiative was a game-changer. Their team's expertise in both strategy and implementation helped us navigate complex challenges and achieve our goals ahead of schedule. Our customer engagement metrics have improved by 60% since implementing their recommendations."
            </TestimonialText>
            <TestimonialAuthor>
              <AuthorImage style={{ backgroundImage: 'url(/assets/images/testimonials/client2.jpg)' }} />
              <AuthorInfo>
                <AuthorName>Michael Chen</AuthorName>
                <AuthorTitle>COO, Global Retail Enterprises</AuthorTitle>
              </AuthorInfo>
            </TestimonialAuthor>
          </Testimonial>
        </TestimonialsSection>
        
        <CTASection>
          <CTATitle>Ready to Transform Your Business Strategy?</CTATitle>
          <CTADescription>
            Let our team of strategic consultants help you navigate challenges, identify opportunities, and develop a roadmap for sustainable growth. Schedule a consultation today to learn how our tailored approach can drive results for your business.
          </CTADescription>
          <Button variant="accent" size="large">Get Started</Button>
        </CTASection>
      </Section>
    </PageContainer>
  );
};

export default StrategicConsulting;
